import { Box, CircularProgress } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { Icon } from 'bench-internal-theme';
import { IconButton } from '@benchlabs/react-design-system';
import Tooltip from '@material-ui/core/Tooltip';
import React, { PropsWithChildren, useCallback, useState } from 'react';
import { useQuery } from 'react-query';

export const vendorTooltipIconSize = 40;

interface VendorDetails {
  vendorName: string;
  vendorDescription: string;
}

const LightTooltip = withStyles(theme => ({
  tooltip: {
    fontFamily: theme.typography.fontFamily,
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.primary,
    fontSize: theme.typography.pxToRem(10),
    fontWeight: 500,
    padding: theme.spacing(1),
    boxShadow:
      '0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12)',
    border: '1px solid rgba(0, 0, 0, 0.12)',

    [`& .MuiTooltip-arrow`]: {
      color: theme.palette.common.white,

      [`&::before`]: {
        border: '1px solid rgba(0, 0, 0, 0.12)',
        color: theme.palette.common.white,
      },
    },
  },
}))(Tooltip);

interface ErrorMessageProps {
  errorMsg?: string;
}

const ErrorMessage = ({
  errorMsg = 'Something went wrong. Please try again later.',
}: PropsWithChildren<ErrorMessageProps>) => {
  return (
    <Box alignItems="center" display="flex">
      <Icon icon="information" color="#CA463E" />
      <Box component="span" ml={1}>
        {errorMsg}
      </Box>
    </Box>
  );
};

const visuallyHidden: React.CSSProperties = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px',
};

const fetchVendorDetails = async (
  transactionId: string,
  apiBasePath: string,
): Promise<VendorDetails | null> => {
  const response = await fetch(`${apiBasePath}/transaction/${transactionId}/vendor-details`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    mode: 'cors',
    credentials: 'include',
  });

  if (response.status === 403 || response.status === 401) {
    throw new Error(
      'You do not have permission to view this information. Please check if you are logged in as the correct user',
    );
  }

  if (!response.ok) {
    throw new Error('Error loading vendor details. Refresh to try again.');
  }

  const jsonData = await response.json();

  // Parse the response to match the expected schema
  if (jsonData.data && jsonData.data.vendorDetails) {
    return jsonData.data.vendorDetails;
  }

  return null;
};

const useVendorDetails = (transactionId: string, apiBasePath: string, enabled = false) => {
  return useQuery<VendorDetails | null, Error>({
    queryKey: ['vendor-details', transactionId],
    queryFn: () => fetchVendorDetails(transactionId, apiBasePath),
    enabled: enabled && !!transactionId && !!apiBasePath,
    retry: 3,
    retryDelay: 1000,
    staleTime: Infinity, // Match the original MFE's staleTime
  });
};

interface VendorDetailsTooltipProps {
  transactionId: string;
  apiBasePath?: string;
}

export const VendorDetailsTooltip = ({ transactionId, apiBasePath }: VendorDetailsTooltipProps) => {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(false);

  // Use provided API base path or fall back to global
  const resolvedApiBasePath = apiBasePath || window.BENCH?.auApi?.VENDOR_DETAILS_API_URL || '';

  const {
    data: vendorDetails,
    isLoading,
    error,
  } = useVendorDetails(transactionId, resolvedApiBasePath, shouldFetch);

  const showTooltipHandler = useCallback(() => {
    setIsTooltipVisible(true);
    setShouldFetch(true);
  }, []);

  const hideTooltipHandler = useCallback(() => {
    setIsTooltipVisible(false);
  }, []);

  const vendorNameAndDescription = vendorDetails
    ? `${vendorDetails.vendorName}: ${vendorDetails.vendorDescription}`
    : 'No description available';

  const tooltipContent = isLoading ? (
    <Box width="200px" display="flex" alignItems="center" justifyContent="center">
      <CircularProgress size={16} />
    </Box>
  ) : error ? (
    <ErrorMessage errorMsg={error.message} />
  ) : (
    vendorNameAndDescription
  );

  return (
    <Box position="relative" width="36px" height="36px">
      <LightTooltip title={tooltipContent} arrow open={isTooltipVisible}>
        <Box>
          <IconButton
            size="small"
            onMouseEnter={showTooltipHandler}
            onTouchStart={showTooltipHandler}
            onFocus={showTooltipHandler}
            aria-label="Show vendor details"
            onMouseLeave={hideTooltipHandler}
            onTouchEnd={hideTooltipHandler}
            onBlur={hideTooltipHandler}
          >
            <Icon icon="information" size="small" />
          </IconButton>
        </Box>
      </LightTooltip>
    </Box>
  );
};

export default visuallyHidden;
